import { HOTBAR_SIZE, type Inventory } from "../player/inventory";
import type { ChestState } from "../player/chest";
import type { FurnaceState } from "../player/furnace";
import { describeStack, type ItemStack } from "../world/items";
import { MODE_LABELS, type GameMode } from "../core/mode";
import { clearBlockIcon, drawItemIcon } from "./icons";
import { createDurabilityBar, updateDurability, type DurabilityView } from "./durability";
import { InventoryScreen, type InventoryMode } from "./inventory-screen";
import { Picker, type PickerItem } from "./picker";
import { Vitals } from "./vitals";
import { sound } from "../audio/sound-engine";

/** Crosshair, hotbar, debug readout, picker, inventory, vitals and overlays. */
export class Hud {
  readonly hotbarEl: HTMLElement;
  readonly picker: Picker;
  readonly vitals: Vitals;
  readonly inventoryScreen: InventoryScreen;

  selected = 0;

  /** Fired when the player clicks Respawn on the death screen. */
  onRespawn: (() => void) | null = null;
  /** Fired when the player chooses creative from the death screen. */
  onSwitchCreative: (() => void) | null = null;
  /** Fired when the player clicks Resume on the pause screen. */
  onResume: (() => void) | null = null;
  /** Fired when the player opens options from the pause screen. */
  onOptions: (() => void) | null = null;
  /** Fired when the player asks to leave the world from the pause screen. */
  onQuitToMenu: (() => void) | null = null;

  private readonly selectedEl: HTMLElement;
  private readonly debugEl: HTMLElement;
  private readonly overlayEl: HTMLElement;
  private readonly underWaterEl: HTMLElement;
  private readonly badgeEl: HTMLElement;
  private readonly toastEl: HTMLElement;
  private readonly breakEl: HTMLElement;
  private readonly breakFillEl: HTMLElement;
  private readonly deathEl: HTMLElement;
  private readonly deathCauseEl: HTMLElement;
  private readonly crosshairEl: HTMLElement;

  private readonly slots: HTMLElement[] = [];
  private readonly icons: HTMLCanvasElement[] = [];
  private readonly countEls: HTMLElement[] = [];
  private readonly iconIds: number[] = [];
  private readonly durViews: DurabilityView[] = [];

  /** Latest hotbar contents pushed by the game (the inventory's first row). */
  private stacks: readonly (ItemStack | null)[] = [];

  constructor(root: HTMLElement) {
    this.hotbarEl = must(root.querySelector<HTMLElement>("#hotbar"));
    this.selectedEl = must(root.querySelector<HTMLElement>("#selected-block"));
    this.debugEl = must(root.querySelector<HTMLElement>("#debug"));
    this.overlayEl = must(root.querySelector<HTMLElement>("#overlay"));
    this.underWaterEl = must(root.querySelector<HTMLElement>("#underwater"));
    this.badgeEl = must(root.querySelector<HTMLElement>("#mode-badge"));
    this.toastEl = must(root.querySelector<HTMLElement>("#toast"));
    this.breakEl = must(root.querySelector<HTMLElement>("#break-progress"));
    this.breakFillEl = must(root.querySelector<HTMLElement>("#break-fill"));
    this.deathEl = must(root.querySelector<HTMLElement>("#death"));
    this.deathCauseEl = must(root.querySelector<HTMLElement>("#death-cause"));
    this.crosshairEl = must(root.querySelector<HTMLElement>("#crosshair"));

    this.picker = new Picker(root);
    this.vitals = new Vitals(root);
    this.inventoryScreen = new InventoryScreen(root);

    this.build();
  }

  private build(): void {
    this.hotbarEl.textContent = "";
    for (let i = 0; i < HOTBAR_SIZE; i++) {
      const slot = document.createElement("div");
      slot.className = "slot";
      slot.dataset.index = String(i);

      const icon = document.createElement("canvas");
      icon.width = 32;
      icon.height = 32;
      icon.className = "slot-icon";

      const key = document.createElement("span");
      key.className = "slot-key";
      key.textContent = String(i + 1);

      const count = document.createElement("span");
      count.className = "slot-count";
      count.style.display = "none";

      const durability = createDurabilityBar();

      slot.append(icon, key, count, durability.bar);
      slot.addEventListener("click", () => {
        sound.playUiClick();
        this.select(i);
      });
      this.hotbarEl.appendChild(slot);

      this.slots.push(slot);
      this.icons.push(icon);
      this.countEls.push(count);
      this.iconIds.push(-1);
      this.durViews.push(durability);
    }

    // `onclick =` rather than addEventListener: the HUD is rebuilt for every
    // world, and these buttons live in the static markup.
    const respawn = must(this.deathEl.querySelector<HTMLButtonElement>("#respawn-btn"));
    respawn.onclick = () => {
      sound.playUiClick();
      this.onRespawn?.();
    };
    const creative = must(this.deathEl.querySelector<HTMLButtonElement>("#switch-creative-btn"));
    creative.onclick = () => {
      sound.playUiClick();
      this.onSwitchCreative?.();
    };
    const resume = must(this.overlayEl.querySelector<HTMLButtonElement>("#resume-btn"));
    resume.onclick = () => {
      sound.playUiClick();
      this.onResume?.();
    };
    const options = this.overlayEl.querySelector<HTMLButtonElement>("#options-btn");
    if (options) {
      options.onclick = () => {
        sound.playUiClick();
        this.onOptions?.();
      };
    }
    const quit = must(this.overlayEl.querySelector<HTMLButtonElement>("#quit-btn"));
    quit.onclick = () => {
      sound.playUiClick();
      this.onQuitToMenu?.();
    };

    this.select(0);
  }

  select(index: number): void {
    this.selected = ((index % HOTBAR_SIZE) + HOTBAR_SIZE) % HOTBAR_SIZE;
    this.slots.forEach((el, i) => el.classList.toggle("active", i === this.selected));
    this.updateSelectedLabel();
  }

  scroll(direction: number): void {
    this.select(this.selected + direction);
  }

  /**
   * Push a hotbar row from the inventory: icon, selection label and — in
   * survival — the live stack count. Creative passes `false` to hide counts.
   */
  setHotbar(stacks: readonly (ItemStack | null)[], showCounts: boolean): void {
    this.stacks = stacks;

    this.slots.forEach((_slot, i) => {
      const stack = stacks[i] ?? null;
      const id = stack?.id ?? 0;
      if (this.iconIds[i] !== id) {
        this.iconIds[i] = id;
        if (id > 0) drawItemIcon(this.icons[i], id);
        else clearBlockIcon(this.icons[i]);
      }
      const countEl = this.countEls[i];
      const show = showCounts && stack !== null;
      countEl.style.display = show ? "block" : "none";
      countEl.textContent = show && stack ? String(stack.count) : "";

      this.slots[i].title = stack
        ? `${describeStack(stack)}${stack.count > 1 ? ` ×${stack.count}` : ""}`
        : "";
      updateDurability(this.durViews[i], stack);
    });

    this.updateSelectedLabel();
  }

  setMode(mode: GameMode): void {
    this.badgeEl.textContent = MODE_LABELS[mode];
    this.badgeEl.dataset.mode = mode;
    this.vitals.setVisible(mode === "survival");
  }

  setVitals(
    health: number,
    maxHealth: number,
    air: number,
    maxAir: number,
    headInWater: boolean,
    food?: number,
    saturation?: number,
  ): void {
    this.vitals.setHealth(health, maxHealth);
    this.vitals.setAir(air, maxAir, headInWater);
    if (food !== undefined) {
      this.vitals.setFood(food, saturation ?? 0);
    }
  }

  setArmor(points: number): void {
    this.vitals.setArmor(points);
  }

  setDamageFlash(intensity: number): void {
    this.vitals.setDamageFlash(intensity);
  }

  setBreakProgress(fraction: number): void {
    if (fraction <= 0) {
      this.breakEl.style.display = "none";
      return;
    }
    this.breakEl.style.display = "block";
    this.breakFillEl.style.width = `${Math.round(Math.min(1, fraction) * 100)}%`;
  }

  toast(text: string): void {
    this.toastEl.textContent = text;
    // Restart the CSS animation even if a toast is already on screen.
    this.toastEl.classList.remove("show");
    void this.toastEl.offsetWidth;
    this.toastEl.classList.add("show");
  }

  setCrosshairVisible(visible: boolean): void {
    this.crosshairEl.style.display = visible ? "block" : "none";
  }

  openPicker(title: string, items: PickerItem[]): void {
    this.picker.show(title, items);
  }

  closePicker(): void {
    this.picker.hide();
  }

  get pickerOpen(): boolean {
    return this.picker.open;
  }

  openInventory(
    inventory: Inventory,
    mode: InventoryMode = "inventory",
    furnace: FurnaceState | null = null,
    chest: ChestState | null = null,
  ): void {
    this.inventoryScreen.show(inventory, mode, furnace, chest);
  }

  closeInventory(): void {
    this.inventoryScreen.hide();
  }

  get inventoryOpen(): boolean {
    return this.inventoryScreen.open;
  }

  get furnaceOpen(): boolean {
    return this.inventoryScreen.open && this.inventoryScreen.screenMode === "furnace";
  }

  refreshFurnace(): void {
    this.inventoryScreen.refreshFurnace();
  }

  showDeath(cause: string): void {
    this.deathCauseEl.textContent = cause;
    this.deathEl.hidden = false;
  }

  hideDeath(): void {
    this.deathEl.hidden = true;
  }

  setDebugVisible(visible: boolean): void {
    this.debugEl.style.display = visible ? "block" : "none";
  }

  setDebug(lines: string[]): void {
    this.debugEl.textContent = lines.join("\n");
  }

  setUnderwater(on: boolean): void {
    this.underWaterEl.style.display = on ? "block" : "none";
  }

  setOverlay(visible: boolean, hint: string): void {
    this.overlayEl.style.display = visible ? "flex" : "none";
    const text = this.overlayEl.querySelector<HTMLElement>(".overlay-hint");
    if (text) text.textContent = hint;
  }

  private updateSelectedLabel(): void {
    const stack = this.stacks[this.selected] ?? null;
    this.selectedEl.textContent = stack ? describeStack(stack) : "Empty";
  }
}

function must<T extends Element>(el: T | null): T {
  if (!el) throw new Error("HUD element missing from index.html");
  return el;
}
